import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, Bookmark, Heart, Loader2, SlidersHorizontal, History, RotateCcw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Drama, VideoMeta } from "@/types/video";
import { usePermissions } from "@/hooks/usePermissions";
import { useDisplayAdjustments, DISPLAY_ADJUSTMENTS_QUERY_KEY } from "@/hooks/useDisplayAdjustments";
import {
  adjustDisplayMetric,
  dramaAdjustment,
  videoLikeAdjustment,
  getDisplayMetricAuditLogs,
  AdjustDisplayMetricParams,
  DisplayMetricAuditLog,
} from "@/services/firebase/displayMetric";

interface Props {
  dramaDocId: string;
  drama: Drama;
  favoritesCount: number; // 欄位值（App 顯示值）
  videos: (VideoMeta & { id: string })[];
  episodeDisplayByVid: Record<string, string>;
}

interface MetricRow {
  key: string;
  label: string;
  icon: React.ReactNode;
  displayValue: number;
  adjustment: number;
  target: Omit<AdjustDisplayMetricParams, "target_value" | "reset" | "reason">;
}

const FIELD_LABEL: Record<string, string> = {
  drama_play_times: "觀看次數",
  favorites_count: "收藏數",
  likeCount: "按讚數",
};

const fmt = (n: number) => n.toLocaleString();
const signed = (n: number) => (n > 0 ? `+${fmt(n)}` : fmt(n));

/**
 * App 顯示數據調整（Admin only）
 * 顯示「App 顯示值 / 累計調整 / 真實值」，可設定目標顯示值或清除調整；每次操作都寫入稽核紀錄。
 */
export default function DisplayMetricsCard({
  dramaDocId,
  drama,
  favoritesCount,
  videos,
  episodeDisplayByVid,
}: Props) {
  const { isAdmin } = usePermissions();
  const queryClient = useQueryClient();
  const { data: adjustments } = useDisplayAdjustments();
  const [editing, setEditing] = useState<{ row: MetricRow; reset: boolean } | null>(null);
  const [targetValue, setTargetValue] = useState("");
  const [reason, setReason] = useState("");
  const [showEpisodes, setShowEpisodes] = useState(false);
  const [showLogs, setShowLogs] = useState(false);

  const { data: auditLogs = [], isLoading: logsLoading } = useQuery({
    queryKey: ["display-metric-audit-logs", dramaDocId],
    queryFn: () => getDisplayMetricAuditLogs(dramaDocId),
    enabled: isAdmin && showLogs,
  });

  const dramaRows: MetricRow[] = useMemo(
    () => [
      {
        key: "drama_play_times",
        label: "觀看次數",
        icon: <Eye className="h-4 w-4 text-blue-500" />,
        displayValue: Number(drama.drama_play_times || 0),
        adjustment: dramaAdjustment(adjustments, dramaDocId, "drama_play_times"),
        target: { target_type: "drama", target_id: dramaDocId, field: "drama_play_times" },
      },
      {
        key: "favorites_count",
        label: "收藏數",
        icon: <Bookmark className="h-4 w-4 text-amber-500" />,
        displayValue: favoritesCount,
        adjustment: dramaAdjustment(adjustments, dramaDocId, "favorites_count"),
        target: { target_type: "drama", target_id: dramaDocId, field: "favorites_count" },
      },
    ],
    [drama.drama_play_times, favoritesCount, adjustments, dramaDocId]
  );

  const episodeRows: MetricRow[] = useMemo(
    () =>
      [...videos]
        .filter((v) => v.id)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map((v) => ({
          key: v.id!,
          label: `第 ${episodeDisplayByVid[v.vid] ?? v.order ?? "?"} 集`,
          icon: <Heart className="h-4 w-4 text-pink-500" />,
          displayValue: Number(v.like || 0),
          adjustment: videoLikeAdjustment(adjustments, dramaDocId, v.id),
          target: { target_type: "video", target_id: v.id!, field: "likeCount" },
        })),
    [videos, adjustments, dramaDocId, episodeDisplayByVid]
  );

  const likeTotals = useMemo(
    () =>
      episodeRows.reduce(
        (acc, r) => ({ display: acc.display + r.displayValue, adjustment: acc.adjustment + r.adjustment }),
        { display: 0, adjustment: 0 }
      ),
    [episodeRows]
  );

  const mutation = useMutation({
    mutationFn: () => {
      if (!editing) throw new Error("未選擇項目");
      if (editing.reset) {
        return adjustDisplayMetric({ ...editing.row.target, reset: true, reason: reason.trim() });
      }
      const value = Number(targetValue);
      if (!Number.isInteger(value) || value < 0) throw new Error("目標顯示值需為 0 以上的整數");
      return adjustDisplayMetric({ ...editing.row.target, target_value: value, reason: reason.trim() });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: DISPLAY_ADJUSTMENTS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: ["drama", dramaDocId] });
      queryClient.invalidateQueries({ queryKey: ["display-metric-audit-logs", dramaDocId] });
      setEditing(null);
    },
  });

  if (!isAdmin) return null;

  const openEditor = (row: MetricRow, reset: boolean) => {
    setEditing({ row, reset });
    setTargetValue(String(row.displayValue));
    setReason("");
    mutation.reset();
  };

  const renderRow = (row: MetricRow) => (
    <tr key={row.key} className="border-t">
      <td className="px-4 py-2 text-sm">
        <div className="flex items-center gap-2">
          {row.icon}
          {row.label}
        </div>
      </td>
      <td className="px-4 py-2 text-sm font-medium text-right">{fmt(row.displayValue)}</td>
      <td className="px-4 py-2 text-sm text-right">
        {row.adjustment === 0 ? (
          <span className="text-muted-foreground">—</span>
        ) : (
          <Badge variant="secondary">{signed(row.adjustment)}</Badge>
        )}
      </td>
      <td className="px-4 py-2 text-sm text-right">{fmt(row.displayValue - row.adjustment)}</td>
      <td className="px-4 py-2 text-right whitespace-nowrap">
        <Button type="button" variant="ghost" size="sm" onClick={() => openEditor(row, false)}>
          調整
        </Button>
        {row.adjustment !== 0 && (
          <Button type="button" variant="ghost" size="sm" onClick={() => openEditor(row, true)}>
            <RotateCcw className="h-3.5 w-3.5 mr-1" />
            清除
          </Button>
        )}
      </td>
    </tr>
  );

  const previewValue = editing
    ? editing.reset
      ? editing.row.displayValue - editing.row.adjustment
      : Number(targetValue)
    : 0;
  const previewValid = editing && Number.isInteger(previewValue) && previewValue >= 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <SlidersHorizontal className="h-5 w-5" />
          App 顯示數據調整
        </CardTitle>
        <CardDescription>
          僅管理員可操作。調整只影響 App 顯示的數字；真實值（顯示值 − 累計調整）保留於系統，
          營收報表與 CMS 統計一律使用真實值。每次調整都會記錄操作者、時間與前後數值，原因可選填。
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="overflow-x-auto border rounded-lg">
          <table className="min-w-full">
            <thead className="bg-muted/50">
              <tr className="text-xs text-muted-foreground">
                <th className="px-4 py-2 text-left font-medium">項目</th>
                <th className="px-4 py-2 text-right font-medium">App 顯示值</th>
                <th className="px-4 py-2 text-right font-medium">累計調整</th>
                <th className="px-4 py-2 text-right font-medium">真實值</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {dramaRows.map(renderRow)}
              <tr className="border-t bg-muted/20">
                <td className="px-4 py-2 text-sm">
                  <button
                    type="button"
                    className="flex items-center gap-2 hover:underline"
                    onClick={() => setShowEpisodes((s) => !s)}
                  >
                    <Heart className="h-4 w-4 text-pink-500" />
                    按讚數（各集加總）{showEpisodes ? "▾" : "▸"}
                  </button>
                </td>
                <td className="px-4 py-2 text-sm font-medium text-right">{fmt(likeTotals.display)}</td>
                <td className="px-4 py-2 text-sm text-right">
                  {likeTotals.adjustment === 0 ? (
                    <span className="text-muted-foreground">—</span>
                  ) : (
                    <Badge variant="secondary">{signed(likeTotals.adjustment)}</Badge>
                  )}
                </td>
                <td className="px-4 py-2 text-sm text-right">{fmt(likeTotals.display - likeTotals.adjustment)}</td>
                <td className="px-4 py-2 text-right text-xs text-muted-foreground">按集調整</td>
              </tr>
              {showEpisodes && episodeRows.map(renderRow)}
            </tbody>
          </table>
        </div>

        <div>
          <Button type="button" variant="outline" size="sm" onClick={() => setShowLogs((s) => !s)}>
            <History className="h-4 w-4 mr-1" />
            {showLogs ? "隱藏調整紀錄" : "查看調整紀錄"}
          </Button>
          {showLogs && (
            <div className="mt-3 overflow-x-auto border rounded-lg">
              {logsLoading ? (
                <div className="p-4 text-sm text-muted-foreground">載入中...</div>
              ) : auditLogs.length === 0 ? (
                <div className="p-4 text-sm text-muted-foreground">尚無調整紀錄</div>
              ) : (
                <table className="min-w-full text-xs">
                  <thead className="bg-muted/50 text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">時間</th>
                      <th className="px-3 py-2 text-left font-medium">操作者</th>
                      <th className="px-3 py-2 text-left font-medium">項目</th>
                      <th className="px-3 py-2 text-right font-medium">顯示值</th>
                      <th className="px-3 py-2 text-right font-medium">當下真實值</th>
                      <th className="px-3 py-2 text-left font-medium">原因</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map((log: DisplayMetricAuditLog) => (
                      <tr key={log.id} className="border-t">
                        <td className="px-3 py-2 whitespace-nowrap">
                          {log.created_at?.toLocaleString("zh-TW", { timeZone: "Asia/Taipei" }) ?? "—"}
                        </td>
                        <td className="px-3 py-2">{log.operator_email ?? "—"}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {log.target_type === "video" ? `第 ${log.episode_order ?? "?"} 集 ` : ""}
                          {FIELD_LABEL[log.field] ?? log.field}
                          {log.action === "reset" && <Badge variant="outline" className="ml-1">清除</Badge>}
                        </td>
                        <td className="px-3 py-2 text-right whitespace-nowrap">
                          {fmt(log.before_value)} → {fmt(log.after_value)}
                        </td>
                        <td className="px-3 py-2 text-right">{fmt(log.real_value)}</td>
                        <td className="px-3 py-2">{log.reason || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
      </CardContent>

      {editing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-[480px] shadow-xl">
            <CardHeader>
              <CardTitle className="text-base">
                {editing.reset ? "清除調整" : "調整 App 顯示值"}：{editing.row.label}
                {editing.row.target.target_type === "video" ? " 按讚數" : ""}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-2 text-center text-xs mb-4">
                <div className="p-2 bg-muted rounded">
                  <div className="text-muted-foreground">目前顯示</div>
                  <div className="text-base font-medium">{fmt(editing.row.displayValue)}</div>
                </div>
                <div className="p-2 bg-muted rounded">
                  <div className="text-muted-foreground">真實值</div>
                  <div className="text-base font-medium">
                    {fmt(editing.row.displayValue - editing.row.adjustment)}
                  </div>
                </div>
                <div className="p-2 bg-muted rounded">
                  <div className="text-muted-foreground">調整後顯示</div>
                  <div className="text-base font-medium">{previewValid ? fmt(previewValue) : "—"}</div>
                </div>
              </div>
              <div className="space-y-3">
                {!editing.reset && (
                  <div>
                    <label className="block text-xs text-muted-foreground mb-1">
                      目標顯示值（之後會隨真實數據繼續累加）
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={targetValue}
                      onChange={(e) => setTargetValue(e.target.value)}
                      className="w-full px-3 py-2 border rounded-lg text-sm bg-background"
                      autoFocus
                    />
                  </div>
                )}
                <div>
                  <label className="block text-xs text-muted-foreground mb-1" htmlFor="display-metric-reason">調整原因（選填）</label>
                  <textarea
                    id="display-metric-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border rounded-lg text-sm bg-background"
                  />
                </div>
              </div>
              <div className="mt-4 p-3 bg-muted rounded text-xs text-muted-foreground">
                將記錄操作者帳號、時間、調整前後數值與當下真實值；原因可留空，紀錄無法修改或刪除。
                App 端可能需數分鐘至數小時（快取）才會看到新數字。
              </div>
              {mutation.error && (
                <p className="mt-3 text-xs text-destructive">{(mutation.error as Error).message}</p>
              )}
              <div className="flex justify-end gap-3 mt-5">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setEditing(null)}
                  disabled={mutation.isPending}
                >
                  取消
                </Button>
                <Button
                  type="button"
                  onClick={() => mutation.mutate()}
                  disabled={mutation.isPending || !previewValid}
                >
                  {mutation.isPending ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      處理中...
                    </>
                  ) : editing.reset ? (
                    "確認清除"
                  ) : (
                    "確認調整"
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </Card>
  );
}
